/**
 * Validation API Service
 *
 * API client for the multi-layer validation system.
 * Endpoints are registered under /api/v1/validation prefix.
 */

import apiClient, { handleApiResponse } from './client';
import type { ApiResponse } from '@/types';
import type {
  SubmissionValidationResult,
  ValidationSummaryResult,
  EmployeeValidationResult,
  NIValidationResult,
  ValidateSubmissionRequest,
  ValidateNIRequest,
  ValidateEmployeeRequest,
  EmployeeValidationData,
} from '@/types/validation';

// =============================================================================
// API RESPONSE TYPES
// =============================================================================

interface BatchValidationResponse {
  total: number;
  valid: number;
  invalid: number;
  results: EmployeeValidationResult[];
}

// =============================================================================
// VALIDATION API
// =============================================================================

export const validationApi = {
  // ===========================================================================
  // FULL SUBMISSION VALIDATION
  // ===========================================================================

  /**
   * Validate a full submission (all employees).
   *
   * POST /api/v1/validation/validate
   *
   * @param data - Submission data including employer info and employees
   * @returns Full validation result with per-employee issues
   */
  validateSubmission: async (
    data: ValidateSubmissionRequest
  ): Promise<SubmissionValidationResult> => {
    const response = await apiClient.post<
      ApiResponse<SubmissionValidationResult>
    >('/v1/validation/validate', data);
    return handleApiResponse(response);
  },

  /**
   * Validate submission and return summary only (no per-employee details).
   *
   * POST /api/v1/validation/validate-summary
   *
   * Useful for quick validation checks when you don't need individual employee issues.
   *
   * @param data - Submission data including employer info and employees
   * @returns Summary validation result
   */
  validateSubmissionSummary: async (
    data: ValidateSubmissionRequest
  ): Promise<ValidationSummaryResult> => {
    const response = await apiClient.post<ApiResponse<ValidationSummaryResult>>(
      '/v1/validation/validate-summary',
      data
    );
    return handleApiResponse(response);
  },

  // ===========================================================================
  // SINGLE NI NUMBER VALIDATION
  // ===========================================================================

  /**
   * Quick validation of a single NI number.
   *
   * POST /api/v1/validation/validate-ni
   *
   * Checks:
   * - Format (8 digits)
   * - Checksum (digit 8 verification)
   * - Invalid NI database (4,478 known invalid numbers from NIB)
   * - Returns typo suggestions via fuzzy matching
   *
   * @param data - NI number to validate
   * @returns NI validation result with suggestions
   */
  validateNI: async (data: ValidateNIRequest): Promise<NIValidationResult> => {
    const response = await apiClient.post<ApiResponse<NIValidationResult>>(
      '/v1/validation/validate-ni',
      data
    );
    return handleApiResponse(response);
  },

  // ===========================================================================
  // SINGLE EMPLOYEE VALIDATION (REAL-TIME)
  // ===========================================================================

  /**
   * Validate a single employee (for real-time form validation).
   *
   * POST /api/v1/validation/validate-employee
   *
   * Use this for immediate feedback as users fill out employee forms.
   *
   * @param data - Employee data with optional submission context
   * @returns Employee validation result
   */
  validateEmployee: async (
    data: ValidateEmployeeRequest
  ): Promise<EmployeeValidationResult> => {
    const response = await apiClient.post<
      ApiResponse<EmployeeValidationResult>
    >('/v1/validation/validate-employee', data);
    return handleApiResponse(response);
  },

  // ===========================================================================
  // BATCH VALIDATION
  // ===========================================================================

  /**
   * Validate multiple employees without full submission context.
   *
   * POST /api/v1/validation/validate-batch
   *
   * Useful for validating a list of employees before adding to a submission.
   * Does not check for duplicates across the batch.
   *
   * @param employees - Array of employee data to validate
   * @returns Batch validation results with summary
   */
  validateBatch: async (
    employees: EmployeeValidationData[]
  ): Promise<BatchValidationResponse> => {
    const response = await apiClient.post<ApiResponse<BatchValidationResponse>>(
      '/v1/validation/validate-batch',
      { employees }
    );
    return handleApiResponse(response);
  },
};

export default validationApi;
